import type {
  OHLCVResponse,
  IntradayResponse,
  IntradayResolution,
  SymbolsResponse,
  SearchResponse,
  Timeframe,
  ScreenerFilterResponse,
  ScreenerFilterParams,
  ScreenerCountsResponse,
  ScreenerSeasonalResponse,
  ScreenerSeasonalV2Response,
  ScreenerSeasonalGranularity,
  ScreenerRelPerfResponse,
  ScreenerRsHistoryResponse,
  QuotesResponse,
  ScreenerSeasonalYearsResponse,
  ScreenerRatingResponse,
  ScreenerIndicesResponse,
  WorldIndicesResponse,
  CommoditiesResponse,
  HomeResponse,
  MhiLatest,
  MhiHistoryRow,
  EventsResponse,
  ForeignHoldingResponse,
  PriceAdjustmentType,
} from '@/types';
import type { FundamentalsResponse, StatementsResponse } from '@/components/Fundamentals/fundamentals.types';

// In production this is the deployed BFF worker URL injected via env/wrangler secret.
// In development Vite proxies /api → localhost:8787 (the BFF worker dev server).
const BFF_BASE =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_BFF_URL
    ? (import.meta.env.VITE_BFF_URL as string).replace(/\/$/, '')
    : '';

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BFF_BASE}${path}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// The MHI endpoints are served by a same-origin Pages Function (functions/api/mhi),
// not the BFF, so they ignore BFF_BASE.
async function getMhi<T>(path: string): Promise<T> {
  const res = await fetch(`/api/mhi/${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

export const api = {
  ohlcv(symbol: string, timeframe: Timeframe = '1D', limit = 500, before?: number, since?: number, adjust?: PriceAdjustmentType[]): Promise<OHLCVResponse> {
    const beforeParam = before !== undefined ? `&before=${before}` : '';
    const sinceParam  = since  !== undefined ? `&since=${since}`   : '';
    const adjustParam = adjust && adjust.length ? `&adjust=${adjust.join(',')}` : '';
    return get<OHLCVResponse>(
      `/api/ohlcv/${encodeURIComponent(symbol)}?timeframe=${timeframe}&limit=${limit}${beforeParam}${sinceParam}${adjustParam}`
    );
  },

  intraday(symbol: string, resolution: IntradayResolution = '1m', limit = 500, before?: number): Promise<IntradayResponse> {
    const beforeParam = before !== undefined ? `&before=${before}` : '';
    return get<IntradayResponse>(
      `/api/intraday/${encodeURIComponent(symbol)}?resolution=${resolution}&limit=${limit}${beforeParam}`
    );
  },

  symbols(): Promise<SymbolsResponse> {
    return get<SymbolsResponse>('/api/symbols');
  },

  search(q: string): Promise<SearchResponse> {
    return get<SearchResponse>(`/api/symbols/search?q=${encodeURIComponent(q)}`);
  },

  screenerFilter(params: ScreenerFilterParams = {}): Promise<ScreenerFilterResponse> {
    const qs = new URLSearchParams();
    if (params.date)             qs.set('date', params.date);
    if (params.above_sma10)      qs.set('above_sma10', '1');
    if (params.above_ema21)      qs.set('above_ema21', '1');
    if (params.above_ema50)      qs.set('above_ema50', '1');
    if (params.above_ema200)     qs.set('above_ema200', '1');
    if (params.is_52w_high)      qs.set('is_52w_high', '1');
    if (params.is_52w_low)       qs.set('is_52w_low', '1');
    if (params.is_hve)           qs.set('is_hve', '1');
    if (params.is_hv1)           qs.set('is_hv1', '1');
    if (params.is_hvytd)         qs.set('is_hvytd', '1');
    if (params.vol_vs_sma50_min !== undefined) qs.set('vol_vs_sma50_min', String(params.vol_vs_sma50_min));
    if (params.vol_vs_sma50_max !== undefined) qs.set('vol_vs_sma50_max', String(params.vol_vs_sma50_max));
    if (params.change_pct_min   !== undefined) qs.set('change_pct_min',   String(params.change_pct_min));
    if (params.change_pct_max   !== undefined) qs.set('change_pct_max',   String(params.change_pct_max));
    if (params.rs_rating_min    !== undefined) qs.set('rs_rating_min',    String(params.rs_rating_min));
    if (params.rs_rating_max    !== undefined) qs.set('rs_rating_max',    String(params.rs_rating_max));
    if (params.rsi14_min        !== undefined) qs.set('rsi14_min',        String(params.rsi14_min));
    if (params.rsi14_max        !== undefined) qs.set('rsi14_max',        String(params.rsi14_max));
    if (params.sort)             qs.set('sort',  params.sort);
    if (params.order)            qs.set('order', params.order);
    if (params.limit)            qs.set('limit', String(params.limit));
    return get<ScreenerFilterResponse>(`/api/screener/filter?${qs}`);
  },

  screenerCounts(limit = 500): Promise<ScreenerCountsResponse> {
    return get<ScreenerCountsResponse>(`/api/screener/counts?limit=${limit}`);
  },

  screenerSeasonal(years: 2 | 5 | 10 | 15 = 5, granularity?: ScreenerSeasonalGranularity): Promise<ScreenerSeasonalResponse> {
    const gran = granularity ? `&granularity=${granularity}` : '';
    return get<ScreenerSeasonalResponse>(`/api/screener/seasonal?years=${years}${gran}`);
  },

  screenerSeasonalV2(years: 2 | 5 | 10 | 15 = 5, granularity: ScreenerSeasonalGranularity = 'monthly', index?: string): Promise<ScreenerSeasonalV2Response> {
    const idxParam = index ? `&index=${encodeURIComponent(index)}` : '';
    return get<ScreenerSeasonalV2Response>(`/api/screener/seasonal?years=${years}&granularity=${granularity}${idxParam}`);
  },

  screenerRsHistory(symbols: string[], weeks = 12): Promise<ScreenerRsHistoryResponse> {
    const syms = encodeURIComponent(symbols.join(','));
    return get<ScreenerRsHistoryResponse>(`/api/screener/rs-history?symbols=${syms}&weeks=${weeks}`);
  },

  /** Batch live quote (price/chg/chgPct) for up to 20 symbols in one request. */
  quotes(symbols: string[]): Promise<QuotesResponse> {
    const syms = encodeURIComponent(symbols.join(','));
    return get<QuotesResponse>(`/api/quotes?symbols=${syms}`);
  },

  screenerSeasonalYears(years: 2 | 5 | 10 | 15 = 5, granularity: ScreenerSeasonalGranularity = 'monthly', index?: string): Promise<ScreenerSeasonalYearsResponse> {
    const idxParam = index ? `&index=${encodeURIComponent(index)}` : '';
    return get<ScreenerSeasonalYearsResponse>(`/api/screener/seasonal-years?years=${years}&granularity=${granularity}${idxParam}`);
  },

  screenerRating(symbol: string): Promise<ScreenerRatingResponse> {
    return get<ScreenerRatingResponse>(`/api/screener/rating?symbol=${encodeURIComponent(symbol)}`);
  },

  screenerIndices(): Promise<ScreenerIndicesResponse> {
    return get<ScreenerIndicesResponse>(`/api/screener/indices`);
  },

  screenerRelPerf(limit = 100): Promise<ScreenerRelPerfResponse> {
    return get<ScreenerRelPerfResponse>(`/api/screener/relative-performance?limit=${limit}`);
  },

  worldIndices(): Promise<WorldIndicesResponse> {
    return get<WorldIndicesResponse>('/api/world-indices');
  },

  commodities(): Promise<CommoditiesResponse> {
    return get<CommoditiesResponse>('/api/commodities');
  },

  home(): Promise<HomeResponse> {
    return get<HomeResponse>('/api/home');
  },

  mhiLatest(): Promise<MhiLatest> {
    return getMhi<MhiLatest>('latest');
  },

  mhiHistory(): Promise<MhiHistoryRow[]> {
    return getMhi<MhiHistoryRow[]>('history');
  },

  fundamentals(symbol: string, fields?: 'all' | string[]): Promise<FundamentalsResponse> {
    const fieldsQuery = !fields
      ? ''
      : `?fields=${encodeURIComponent(fields === 'all' ? 'all' : fields.join(','))}`;
    return get<FundamentalsResponse>(`/api/fundamentals/${encodeURIComponent(symbol)}${fieldsQuery}`);
  },

  // Full income/balance/cash-flow line items + shareholder register, read
  // directly from R2. Requires a logged-in session — returns 401 for
  // anonymous callers (see functions/_middleware.ts).
  statements(symbol: string): Promise<StatementsResponse> {
    return get<StatementsResponse>(`/api/statements/${encodeURIComponent(symbol)}`);
  },

  events(symbol: string): Promise<EventsResponse> {
    return get<EventsResponse>(`/api/events/${encodeURIComponent(symbol)}`);
  },

  foreignHolding(symbol: string): Promise<ForeignHoldingResponse> {
    return get<ForeignHoldingResponse>(`/api/foreign-holding/${encodeURIComponent(symbol)}`);
  },

  macroSeries(): Promise<{ series: import('@/store/macroStore').MacroSeries[] }> {
    return get('/api/macro/series');
  },

  macroData(seriesIds: string[], from = '2015-01-01'): Promise<{ data: Record<string, import('@/store/macroStore').MacroDataPoint[]> }> {
    const ids = seriesIds.map(encodeURIComponent).join(',');
    return get(`/api/macro/data?series_ids=${ids}&from=${from}`);
  },
};
